import { useCallback, useEffect, useMemo, useState } from 'react';
import { Combobox, Field, Option } from '@fluentui/react-components';
import { editWorkItemStyles } from '../styles';
import { useAutomationRuleActions } from '../../use-actions';
import { ActionItemType, EditWorkItemActionType } from '../../types';
import ClearebleButtonInput from 'src/components/shared/cleareble-button-input';

interface Props {
  id?: string;
  actionId: string;
  validationError: string | null;
}

export const defaultFieldActionOptions = [
	{ label: 'Set Value', value: 'set-value' },
	{ label: 'Clear Field', value: 'clear-field' },
	{ label: 'Copy value from another field', value: 'copy-field-value' },
	{ label: 'Copy value from parent work item', value: 'copy-field-value-from-parent-work-item' }
];

export default function FieldActionInput(props: Props) {
	const classes = editWorkItemStyles();

	const {actions, setActions, resetValidationErrors} = useAutomationRuleActions();

	const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
	const [status, setStatus] = useState<'searching' | 'selected'>('selected');
	const [text, setText] = useState<string>('');

	const current: EditWorkItemActionType = useMemo(() => {
		const foundAction = actions.find(item => item.id === props.actionId) as ActionItemType;
		return foundAction!.editWorkItem as EditWorkItemActionType;
	}, [actions, props.actionId]);

	const onChangeText = useCallback((value: string) => {
		setStatus('searching');
		setText(value);
		
		if (value === '') {
			getOptions();
		}
		
		if (value !== '') {
			const filteredOptions = options.filter(item => item.label.toLowerCase().includes(value.toLowerCase()));
			setOptions(filteredOptions);
		}
	}, [options]);

	const setOptionSelect = useCallback((value: string) => {
		if (value) {
			resetValidationErrors();
			const foundAction = actions.find(item => item.id === props.actionId);
			setStatus('selected');
	
			if (foundAction) {
				setActions(oldActions => {
					const current = oldActions.find(item => item.id === props.actionId) as ActionItemType;
					const others = oldActions.filter(item => item.id !== props.actionId);
		
					return [...others, {
						...current, editWorkItem: {
							...current.editWorkItem as EditWorkItemActionType,
							fieldActionValue: value,
						}
					}];
				});
			}
		}
	}, []);
  
	const valueRender = useMemo(() => {
		if (current && current.fieldActionValue && status === 'selected') {
			return options.find(item => item.value === current.fieldActionValue)?.label;
		}

		if (text) {
			return text;
		}

		return '';
	}, [text, current.fieldActionValue, options, status]);

	const selectedOptions = useMemo(() => {
		const filteredValues = options.filter(item => current.fieldActionValue === item.value);

		if (current.fieldActionValue && filteredValues.length > 0) {
			return filteredValues.map(item => item.value);
		}

		return [];
	}, [current, options]);

	const getOptions = useCallback(async () => {
		setOptions(defaultFieldActionOptions);
	}, []);

	const clearOptionSelect = useCallback(() => {
		const foundAction = actions.find(item => item.id === props.actionId);
		setStatus('selected');

		if (foundAction) {
			setActions(oldActions => {
				const current = oldActions.find(item => item.id === props.actionId) as ActionItemType;
				const others = oldActions.filter(item => item.id !== props.actionId);

				return [...others, {
					...current, editWorkItem: {
						...current.editWorkItem as EditWorkItemActionType,
						fieldActionValue: ''
					}
				}];
			});
		}
	}, [actions, props.actionId]);

	useEffect(() => {
		getOptions();
	}, []);
  
	return (
		<Field
			className={classes.dropdownRoot}
			validationMessage={props.validationError}
		>
			<Combobox
				clearable
				inputMode='search'
				autoComplete='off'
				className={classes.dropdown}
				appearance='outline'
				placeholder='Choose a value'
				positioning="below-start"
				value={valueRender}
				selectedOptions={selectedOptions}
				clearIcon={<ClearebleButtonInput onClick={clearOptionSelect} />}
				onOptionSelect={(_, data) => {
					setOptionSelect(data.optionValue as string);
				}}
				onChange={(ev) => {
					onChangeText(ev.target.value);
				}}
				onBlur={() => {
					setStatus('selected');
					getOptions();
				}}
			>
				<div style={{maxHeight: 240}}>
					{options.map((item, idx) => (
						<Option key={idx} value={item.value}>{item.label}</Option>
					))}
				</div>
			</Combobox>
		</Field>
	);
}